import type { enBackoffice } from '../en/backoffice';

export const idBackoffice: Record<keyof typeof enBackoffice, string> = {
  // ---- Navigation & shared ------------------------------------------------------------
  'bo.nav.dashboard': 'Dasbor',
  'bo.nav.group.overview': 'Ringkasan',
  'bo.nav.group.tables': 'Meja',
  'bo.nav.group.finance': 'Keuangan',
  'bo.nav.group.approvalQueue': 'Antrian',
  'bo.nav.group.branchSettings': 'Pengaturan cabang',
  'bo.nav.floors': 'Lantai & meja',
  'bo.nav.menu': 'Menu',
  'bo.nav.stations': 'Stasiun dapur',
  'bo.nav.staff': 'Staf',
  'bo.nav.shifts': 'Shift kas',
  'bo.nav.reports': 'Laporan',
  'bo.nav.branches': 'Cabang',
  'bo.nav.approvals': 'Persetujuan',
  'bo.nav.approvalsBadge': '{count} menunggu Anda',
  'bo.nav.budgets': 'Anggaran',
  'bo.nav.approvalPolicies': 'Kebijakan persetujuan',
  'bo.nav.ap': 'Hutang usaha',
  'bo.nav.apSections': 'Bagian hutang usaha',
  'bo.nav.apInvoices': 'Faktur',
  'bo.nav.apPayments': 'Pembayaran',
  'bo.nav.apCreditNotes': 'Nota kredit',
  'bo.nav.apAging': 'Umur hutang',
  'bo.nav.apSettings': 'Pengaturan pencocokan',
  'bo.nav.reconciliation': 'Rekonsiliasi',
  'bo.nav.settings': 'Pengaturan',
  'bo.noBranch.text':
    'Halaman ini bekerja per cabang, dan belum ada cabang. Buat cabang terlebih dahulu — datanya akan muncul di sini setelah itu.',
  'bo.noBranch.link': 'Buka Cabang',
  'bo.col.active': 'Aktif',
  'bo.col.actions': 'Tindakan',
  'bo.inactiveOption': '{name} (nonaktif)',
  'bo.form.nameRequired': 'Masukkan nama.',
  'bo.form.tooLong': 'Gunakan paling banyak {max} karakter.',
  'bo.form.sortOrder': 'Urutan',
  'bo.form.sortOrderHint': 'Angka lebih kecil tampil lebih dulu.',
  'bo.form.sortInvalid': 'Masukkan bilangan bulat (0 atau lebih).',

  // ---- Ranges ------------------------------------------------------------------------
  'bo.range.label': 'Rentang tanggal',
  'bo.range.1d': 'Hari ini',
  'bo.range.7d': '7 hari',
  'bo.range.30d': '30 hari',
  'bo.range.between': '{from} – {to}',

  // ---- Metrics -----------------------------------------------------------------------
  'bo.metric.grossSales': 'Penjualan kotor',
  'bo.metric.grossSales.desc': 'Harga item termasuk modifier, sebelum biaya layanan dan pajak.',
  'bo.metric.grossSales.hint': 'Dari {bills} tagihan lunas',
  'bo.metric.bills': 'Tagihan',
  'bo.metric.bills.desc': 'Tagihan lunas dalam rentang ini.',
  'bo.metric.averageCheck': 'Rata-rata tagihan',
  'bo.metric.averageCheck.desc': 'Total diterima per tagihan lunas.',
  'bo.metric.guests': 'Tamu',
  'bo.metric.guests.desc': 'Jumlah tamu pada tagihan lunas.',
  'bo.metric.itemsSold': 'Item terjual',
  'bo.metric.itemsSold.desc': 'Jumlah item yang ditagihkan.',
  'bo.metric.totalCollected': 'Total diterima',
  'bo.metric.totalCollected.desc': 'Yang dibayar tamu: penjualan + layanan + pajak + pembulatan.',
  'bo.metric.serviceCharge': 'Biaya layanan',
  'bo.metric.serviceCharge.desc': 'Biaya layanan pada tagihan lunas.',
  'bo.metric.tax': 'Pajak',
  'bo.metric.tax.desc': 'Pajak restoran (PB1) pada tagihan lunas.',
  'bo.metric.rounding': 'Pembulatan',
  'bo.metric.cancelledItems': 'Item dibatalkan',
  'bo.metric.cancelledItems.desc': 'Item yang dibatalkan setelah dikirim ke dapur.',

  // ---- Dashboard ---------------------------------------------------------------------
  'bo.dashboard.title': 'Dasbor',
  'bo.dashboard.subtitle': '{branch} · {range}',
  'bo.dashboard.noPermission':
    'Peran Anda tidak dapat melihat angka penjualan. Gunakan menu di kiri untuk area yang Anda kelola.',
  'bo.dashboard.empty':
    'Belum ada penjualan pada rentang ini — data muncul setelah tagihan pertama lunas.',
  'bo.chart.titleDaily': 'Penjualan kotor per hari',
  'bo.chart.titleHourly': 'Penjualan kotor per jam',
  'bo.chart.description': 'Arahkan kursor atau fokus ke grafik, lalu gunakan ← → untuk tiap titik.',
  'bo.chart.billsInTooltip': 'Tagihan (di tooltip)',
  'bo.chart.endLabel': 'Penjualan',
  'bo.chart.ariaDaily':
    'Grafik area penjualan kotor per hari, {range}. Total {total}; tertinggi {peak} pada {peakAt}.',
  'bo.chart.ariaHourly':
    'Grafik area penjualan kotor per jam, {range}. Total {total}; tertinggi {peak} pukul {peakAt}.',
  'bo.chart.keyboardHint': '{label} Gunakan tombol panah kiri dan kanan untuk membaca tiap titik.',
  'bo.chart.pointAnnounce': '{title}: penjualan kotor {value}, {bills} tagihan',
  'bo.topItems.title': 'Item terlaris',
  'bo.topItems.description': 'Terlaris menurut penjualan kotor.',
  'bo.topItems.meta': '{quantity} terjual',
  'bo.byCategory.title': 'Penjualan per kategori',
  'bo.byCategory.description': 'Penjualan kotor per kategori menu.',

  // ---- Floors & tables ---------------------------------------------------------------
  'bo.floors.title': 'Lantai & meja',
  'bo.floors.subtitle': 'Area makan dan meja di {branch}.',
  'bo.floors.readOnly': 'Peran Anda dapat melihat denah, tetapi tidak dapat mengubahnya.',
  'bo.floors.add': 'Tambah lantai',
  'bo.floors.listTitle': 'Lantai',
  'bo.floors.listDesc': 'Area yang mengelompokkan meja (mis. dalam ruangan, teras).',
  'bo.floors.empty': 'Belum ada lantai — tambahkan lantai, lalu tambahkan mejanya.',
  'bo.floors.meta': '{count} meja · urutan {order}',
  'bo.floors.editNamed': 'Ubah lantai {name}',
  'bo.floors.createTitle': 'Tambah lantai',
  'bo.floors.editTitle': 'Ubah lantai',
  'bo.floors.formDesc': 'Lantai mengelompokkan meja di layar pelayan dan kasir.',
  'bo.floors.name': 'Nama lantai',
  'bo.floors.saved': 'Lantai {name} disimpan.',
  'bo.tables.add': 'Tambah meja',
  'bo.tables.listTitle': 'Meja',
  'bo.tables.listDesc':
    'Status selalu terkini; di sini hanya tersedia ↔ tidak dipakai yang bisa diatur.',
  'bo.tables.empty': 'Belum ada meja — tambahkan meja agar pelayan dapat menempatkan tamu.',
  'bo.tables.emptyNoFloor': 'Tambahkan lantai terlebih dahulu; setiap meja berada di satu lantai.',
  'bo.tables.col.name': 'Meja',
  'bo.tables.col.floor': 'Lantai',
  'bo.tables.col.capacity': 'Kursi',
  'bo.tables.col.status': 'Status',
  'bo.tables.markOutOfService': 'Tidak dipakai',
  'bo.tables.markAvailable': 'Jadikan tersedia',
  'bo.tables.statusChanged': 'Meja {name} sekarang {status}.',
  'bo.tables.editNamed': 'Ubah meja {name}',
  'bo.tables.createTitle': 'Tambah meja',
  'bo.tables.editTitle': 'Ubah meja',
  'bo.tables.formDesc': 'Nama dan jumlah kursi seperti yang dilihat pelayan.',
  'bo.tables.name': 'Nama meja',
  'bo.tables.nameHint': 'Singkat, mis. M1 atau T4.',
  'bo.tables.floorRequired': 'Pilih lantai.',
  'bo.tables.capacityHint': '1 sampai 50 kursi.',
  'bo.tables.capacityInvalid': 'Masukkan jumlah kursi dari 1 sampai 50.',
  'bo.tables.activeLabel': 'Meja dipakai',
  'bo.tables.activeHint': 'Meja nonaktif disembunyikan dari denah.',
  'bo.tables.saved': 'Meja {name} disimpan.',

  // ---- Menu --------------------------------------------------------------------------
  'bo.menu.title': 'Menu',
  'bo.menu.subtitle': 'Item, kategori, dan modifier yang dijual di {branch}.',
  'bo.menu.readOnly': 'Peran Anda dapat melihat menu, tetapi tidak dapat mengubahnya.',
  'bo.menu.tabs': 'Bagian menu',
  'bo.menu.tab.items': 'Item',
  'bo.menu.tab.categories': 'Kategori',
  'bo.menu.tab.modifiers': 'Grup modifier',
  'bo.categories.title': 'Kategori',
  'bo.categories.description': 'Mengelompokkan item di menu dan mengarahkannya ke stasiun dapur.',
  'bo.categories.add': 'Tambah kategori',
  'bo.categories.empty': 'Belum ada kategori — tambahkan satu sebelum menambah item menu.',
  'bo.categories.col.name': 'Kategori',
  'bo.categories.col.station': 'Stasiun bawaan',
  'bo.categories.col.items': 'Item',
  'bo.categories.editNamed': 'Ubah kategori {name}',
  'bo.categories.createTitle': 'Tambah kategori',
  'bo.categories.editTitle': 'Ubah kategori',
  'bo.categories.formDesc':
    'Item dalam kategori ini dikirim ke stasiun bawaannya, kecuali item punya stasiun sendiri.',
  'bo.categories.stationHint': 'Tempat tiket kategori ini disiapkan.',
  'bo.categories.stationNone': 'Stasiun bawaan cabang',
  'bo.categories.activeLabel': 'Kategori dipakai',
  'bo.categories.activeHint': 'Kategori nonaktif beserta itemnya disembunyikan saat memesan.',
  'bo.categories.saved': 'Kategori {name} disimpan.',
  'bo.items.title': 'Item menu',
  'bo.items.description': 'Harga per porsi dalam mata uang cabang.',
  'bo.items.add': 'Tambah item',
  'bo.items.filter': 'Saring menurut kategori',
  'bo.items.allCategories': 'Semua kategori',
  'bo.items.empty': 'Belum ada item di kategori ini — tambahkan item untuk mulai menjualnya.',
  'bo.items.emptyNoCategory':
    'Tambahkan kategori terlebih dahulu; setiap item berada di satu kategori.',
  'bo.items.col.name': 'Item',
  'bo.items.col.category': 'Kategori',
  'bo.items.col.station': 'Stasiun',
  'bo.items.col.price': 'Harga',
  'bo.items.col.prep': 'Waktu siap',
  'bo.items.col.availability': 'Ketersediaan',
  'bo.items.available': 'Tersedia',
  'bo.items.soldOut': 'Habis',
  'bo.items.markSoldOut': 'Tandai habis',
  'bo.items.markAvailable': 'Tandai tersedia',
  'bo.items.nowSoldOut': '{name} ditandai habis.',
  'bo.items.nowAvailable': '{name} tersedia lagi.',
  'bo.items.stationViaCategory': '{name} (kategori)',
  'bo.items.stationDefault': 'Bawaan cabang',
  'bo.items.editNamed': 'Ubah {name}',
  'bo.items.createTitle': 'Tambah item menu',
  'bo.items.editTitle': 'Ubah item menu',
  'bo.items.formDesc': 'Tamu dan staf melihat nama, deskripsi, dan harganya.',
  'bo.items.descriptionLabel': 'Deskripsi (opsional)',
  'bo.items.imageUrlLabel': 'URL foto',
  'bo.items.imageUrlHint': 'Tautan https ke foto, mis. dari Unsplash. Kosongkan jika tanpa foto.',
  'bo.items.imageUrlInvalid': 'Masukkan tautan lengkap https://.',
  'bo.items.imageCreditNameLabel': 'Kredit foto',
  'bo.items.imageCreditUrlLabel': 'Tautan kredit',
  'bo.items.categoryRequired': 'Pilih kategori.',
  'bo.items.priceLabel': 'Harga ({currency}, satuan terkecil)',
  'bo.items.priceHint':
    'Hanya angka, dalam satuan terkecil — untuk IDR berarti rupiah, mis. 25000.',
  'bo.items.priceRequired': 'Masukkan harga.',
  'bo.items.priceInvalid': 'Masukkan harga dengan angka saja.',
  'bo.items.priceNoPermission': 'Peran Anda tidak dapat mengubah harga.',
  'bo.items.pricePreview': 'Ditampilkan sebagai',
  'bo.items.stationHint': 'Biarkan ikut kategori, kecuali item ini dibuat di tempat lain.',
  'bo.items.stationFromCategory': 'Ikuti stasiun kategori',
  'bo.items.prepLabel': 'Waktu siap dalam menit (opsional)',
  'bo.items.prepHint': 'Dipakai untuk menandai tiket yang terlambat di dapur.',
  'bo.items.prepInvalid': 'Masukkan 0 sampai 240 menit, atau kosongkan.',
  'bo.items.modifiersLabel': 'Grup modifier',
  'bo.items.modifiersNone': 'Belum ada grup modifier — buat di tab Grup modifier.',
  'bo.items.availableLabel': 'Bisa dipesan',
  'bo.items.availableHint': 'Hapus centang jika habis untuk hari ini.',
  'bo.items.activeLabel': 'Tampil di menu',
  'bo.items.activeHint': 'Item nonaktif disembunyikan sepenuhnya dari menu.',
  'bo.items.saved': '{name} disimpan.',
  'bo.modifiers.title': 'Grup modifier',
  'bo.modifiers.description':
    'Pilihan tamu untuk sebuah item, seperti tingkat pedas atau tambahan.',
  'bo.modifiers.add': 'Tambah grup modifier',
  'bo.modifiers.empty': 'Belum ada grup modifier — tambahkan satu, lalu pasang ke item menu.',
  'bo.modifiers.col.name': 'Grup',
  'bo.modifiers.col.rule': 'Pilihan',
  'bo.modifiers.col.options': 'Opsi aktif',
  'bo.modifiers.col.items': 'Item',
  'bo.modifiers.required': 'Wajib',
  'bo.modifiers.inactiveCount': '+ {count} nonaktif',
  'bo.modifiers.ruleExactly': 'Pilih tepat {count}',
  'bo.modifiers.ruleUpTo': 'Pilih hingga {max}',
  'bo.modifiers.ruleBetween': 'Pilih {min}–{max}',
  'bo.modifiers.editNamed': 'Ubah grup modifier {name}',
  'bo.modifiers.createTitle': 'Tambah grup modifier',
  'bo.modifiers.editTitle': 'Ubah grup modifier',
  'bo.modifiers.formDesc': 'Menyimpan akan mengirim daftar opsi lengkap.',
  'bo.modifiers.nameHint': 'Tampil ke staf saat memesan, mis. "Tingkat pedas".',
  'bo.modifiers.minLabel': 'Pilihan minimum',
  'bo.modifiers.minHint': '0 = opsional; 1 atau lebih = wajib.',
  'bo.modifiers.maxLabel': 'Pilihan maksimum',
  'bo.modifiers.maxHint': 'Minimal 1, maksimal 20.',
  'bo.modifiers.minInvalid': 'Masukkan bilangan bulat dari 0 sampai 20.',
  'bo.modifiers.maxInvalid': 'Masukkan bilangan bulat dari 1 sampai 20.',
  'bo.modifiers.minAboveMax': 'Minimum tidak boleh lebih besar dari maksimum.',
  'bo.modifiers.optionsLabel': 'Opsi',
  'bo.modifiers.optionsHint':
    'Opsi yang dihapus dari daftar ini dinonaktifkan, bukan dihapus — pesanan lama tetap menyimpannya. Atau hapus centang "Aktif" untuk menyembunyikan opsi.',
  'bo.modifiers.optionName': 'Opsi',
  'bo.modifiers.optionPrice': 'Perubahan harga',
  'bo.modifiers.optionActive': 'Aktif',
  'bo.modifiers.priceInvalid': 'Hanya angka; awali dengan minus untuk potongan.',
  'bo.modifiers.noExtraCharge': 'Tanpa biaya tambahan',
  'bo.modifiers.optionsRequired': 'Tambahkan setidaknya satu opsi.',
  'bo.modifiers.optionsTooMany': 'Gunakan paling banyak 40 opsi.',
  'bo.modifiers.removeOption': 'Hapus opsi {name}',
  'bo.modifiers.removedWarning':
    '{count} opsi lama yang dihapus dari daftar akan dinonaktifkan saat Anda menyimpan.',
  'bo.modifiers.addOption': 'Tambah opsi',
  'bo.modifiers.activeLabel': 'Grup dipakai',
  'bo.modifiers.activeHint': 'Grup nonaktif tidak ditawarkan saat memesan.',
  'bo.modifiers.saved': 'Grup modifier {name} disimpan.',
  'bo.menu.filter.allStations': 'Semua stasiun',
  'bo.menu.filter.allStatuses': 'Semua status',
  'bo.menu.filter.allAvailability': 'Semua ketersediaan',
  'bo.menu.filter.allRules': 'Semua aturan pilihan',
  'bo.categories.searchPlaceholder': 'Kategori atau stasiun',
  'bo.items.searchPlaceholder': 'Item, deskripsi, kategori, atau stasiun',
  'bo.items.emptyNone': 'Belum ada item menu — tambahkan item untuk mulai menjualnya.',
  'bo.items.col.description': 'Deskripsi',
  'bo.modifiers.searchPlaceholder': 'Grup atau opsi',

  // ---- Kitchen stations --------------------------------------------------------------
  'bo.stations.title': 'Stasiun dapur',
  'bo.stations.subtitle': 'Tempat tiket {branch} disiapkan.',
  'bo.stations.readOnly': 'Peran Anda dapat melihat stasiun, tetapi tidak dapat mengubahnya.',
  'bo.stations.add': 'Tambah stasiun',
  'bo.stations.listTitle': 'Stasiun',
  'bo.stations.listDesc': 'Setiap stasiun punya daftar tiket sendiri di layar dapur.',
  'bo.stations.empty':
    'Belum ada stasiun — tambahkan satu agar layar dapur dapat menampilkan tiket.',
  'bo.stations.col.name': 'Stasiun',
  'bo.stations.col.default': 'Bawaan',
  'bo.stations.col.items': 'Item diarahkan',
  'bo.stations.defaultBadge': 'Bawaan',
  'bo.stations.editNamed': 'Ubah stasiun {name}',
  'bo.stations.createTitle': 'Tambah stasiun',
  'bo.stations.editTitle': 'Ubah stasiun',
  'bo.stations.formDesc':
    'Item dikirim ke stasiunnya sendiri, jika tidak ke stasiun kategorinya, jika tidak ke stasiun bawaan.',
  'bo.stations.nameHint': 'Mis. Dapur Utama, Bar, Panggangan.',
  'bo.stations.defaultLabel': 'Stasiun bawaan',
  'bo.stations.defaultHint':
    'Menerima item yang tidak punya stasiun sendiri maupun dari kategorinya.',
  'bo.stations.activeLabel': 'Stasiun dipakai',
  'bo.stations.activeHint': 'Stasiun nonaktif tidak lagi menerima tiket.',
  'bo.stations.saved': 'Stasiun {name} disimpan.',

  // ---- Staff -------------------------------------------------------------------------
  'bo.staff.title': 'Staf',
  'bo.staff.subtitle': 'Orang yang dapat masuk ke {tenant}.',
  'bo.staff.readOnly': 'Peran Anda dapat melihat staf, tetapi tidak dapat menambahkannya.',
  'bo.staff.add': 'Tambah staf',
  'bo.staff.listTitle': 'Anggota staf',
  'bo.staff.listDesc': 'Peran menentukan antarmuka dan tindakan yang bisa dipakai setiap orang.',
  'bo.staff.empty': 'Belum ada staf — tambahkan staf agar mereka bisa masuk.',
  'bo.staff.col.name': 'Nama',
  'bo.staff.col.roles': 'Peran',
  'bo.staff.col.status': 'Status',
  'bo.staff.col.actions': 'Tindakan',
  'bo.staff.roles.manage': 'Peran',
  'bo.staff.roles.manageFor': 'Kelola peran {name}',
  'bo.staff.roles.title': 'Peran {name}',
  'bo.staff.roles.desc':
    'Peran bisa digabung. Tim kecil dapat memberi tugas gudang kepada orang yang sudah ada — mis. Juru masak + Kepala gudang.',
  'bo.staff.roles.addRole': 'Tambah peran',
  'bo.staff.roles.add': 'Tambah peran',
  'bo.staff.roles.remove': 'Hapus {role}',
  'bo.staff.roles.lastRole': 'Setiap staf harus punya setidaknya satu peran.',
  'bo.staff.roles.granted': 'Peran ditambahkan.',
  'bo.staff.roles.revoked': 'Peran dihapus.',
  'bo.staff.roleAt': '{role} · {branch}',
  'bo.staff.allBranches': 'Semua cabang',
  'bo.staff.allBranchesHint': 'Peran berlaku di semua cabang, termasuk cabang baru nanti.',
  'bo.staff.status.active': 'Aktif',
  'bo.staff.status.invited': 'Diundang',
  'bo.staff.status.deactivated': 'Dinonaktifkan',
  'bo.staff.createTitle': 'Tambah anggota staf',
  'bo.staff.formDesc': 'Mereka masuk dengan email ini dan kata sandi awal.',
  'bo.staff.email': 'Email',
  'bo.staff.emailRequired': 'Masukkan alamat email.',
  'bo.staff.emailInvalid': 'Masukkan alamat email yang valid.',
  'bo.staff.emailTaken': 'Orang ini sudah menjadi anggota staf.',
  'bo.staff.password': 'Kata sandi awal',
  'bo.staff.passwordHint': 'Minimal 10 karakter. Diabaikan jika email ini sudah punya akun.',
  'bo.staff.passwordTooShort': 'Gunakan minimal 10 karakter.',
  'bo.staff.role': 'Peran',
  'bo.staff.branch': 'Cabang',
  'bo.staff.submit': 'Tambah anggota staf',
  'bo.staff.saved': '{name} telah ditambahkan.',
  'bo.role.manager': 'Manajer',
  'bo.role.cashier': 'Kasir',
  'bo.role.waiter': 'Pelayan',
  'bo.role.host': 'Penerima tamu',
  'bo.role.chef': 'Juru masak',
  'bo.role.inventory': 'Persediaan',
  'bo.role.warehouse_manager': 'Kepala gudang',
  'bo.role.purchasing': 'Pembelian',
  'bo.role.administrator': 'Administrator',
  'bo.role.owner': 'Pemilik',

  // ---- Cash shifts -------------------------------------------------------------------
  'bo.shifts.title': 'Shift kas',
  'bo.shifts.subtitle':
    'Riwayat laci kas {branch}. Waktu dalam {timeZone}. Pembayaran delivery masuk ke platform dan tidak dihitung sebagai tunai.',
  'bo.shifts.listTitle': 'Riwayat shift',
  'bo.shifts.listDesc':
    'Seharusnya = modal awal + penjualan tunai + kas masuk − kas keluar. Selisih = dihitung − seharusnya.',
  'bo.shifts.empty': 'Belum ada shift kas — muncul setelah kasir membuka shift.',
  'bo.shifts.col.cashier': 'Kasir',
  'bo.shifts.col.opened': 'Dibuka',
  'bo.shifts.col.closed': 'Ditutup',
  'bo.shifts.col.float': 'Modal awal',
  'bo.shifts.col.cashSales': 'Penjualan tunai',
  'bo.shifts.col.payInOut': 'Kas masuk / keluar',
  'bo.shifts.col.expected': 'Seharusnya',
  'bo.shifts.col.counted': 'Dihitung',
  'bo.shifts.col.discrepancy': 'Selisih',
  'bo.shifts.col.status': 'Status',
  'bo.shifts.payments': '{count} pembayaran',
  'bo.shifts.short': 'Kurang',
  'bo.shifts.over': 'Lebih',

  // ---- Reports -----------------------------------------------------------------------
  'bo.reports.title': 'Laporan',
  'bo.reports.subtitle': 'Penjualan {branch} per tanggal bisnis ({timeZone}).',
  'bo.reports.rangeTitle': 'Rentang tanggal',
  'bo.reports.rangeDesc': 'Tanggal bisnis menurut zona waktu cabang, kedua hari termasuk.',
  'bo.reports.from': 'Dari',
  'bo.reports.to': 'Sampai',
  'bo.reports.error.order': 'Tanggal awal harus sama dengan atau sebelum tanggal akhir.',
  'bo.reports.error.tooLong': 'Pilih paling banyak {max} hari.',
  'bo.reports.error.future': 'Tanggal akhir tidak boleh di masa depan.',
  'bo.reports.error.incomplete': 'Isi kedua tanggal.',
  'bo.reports.totalsTitle': 'Total',
  'bo.reports.totalsDesc': 'Tagihan lunas, {range}.',
  'bo.reports.col.metric': 'Ukuran',
  'bo.reports.col.value': 'Nilai',
  'bo.reports.col.date': 'Tanggal',
  'bo.reports.col.method': 'Metode',
  'bo.reports.col.amount': 'Jumlah',
  'bo.reports.col.category': 'Kategori',
  'bo.reports.col.item': 'Item',
  'bo.reports.col.quantity': 'Jml',
  'bo.reports.byDayTitle': 'Per hari',
  'bo.reports.byDayDesc': 'Hari dengan tagihan lunas.',
  'bo.reports.byPaymentTitle': 'Per metode pembayaran',
  'bo.reports.byPaymentDesc': 'Jumlah yang diterima pada tagihan lunas.',
  'bo.reports.method.cash': 'Tunai',
  'bo.reports.method.platform': 'Platform delivery',
  'bo.reports.byChannelTitle': 'Penjualan per kanal',
  'bo.reports.byChannelDesc':
    'Tagihan lunas per jenis pesanan dan platform delivery. Pesanan delivery dibayar ke platform, bukan ke laci kas.',
  'bo.reports.col.channel': 'Kanal',
  'bo.reports.col.collected': 'Diterima',
  'bo.reports.definitionsTitle': 'Definisi',
  'bo.reports.definitionsDesc':
    'Cara setiap angka dihitung, sebagaimana dikirim bersama laporan ini.',
  'bo.reports.definitionsNone': 'Server tidak mengirim definisi untuk laporan ini.',
  'bo.reports.notPnl':
    'Penjualan operasional dari tagihan lunas — bukan laporan laba rugi akuntansi.',
  'bo.reports.def.scope': 'Cakupan',

  // ---- Branches ----------------------------------------------------------------------
  'bo.branches.title': 'Cabang',
  'bo.branches.subtitle': 'Restoran milik {tenant}.',
  'bo.branches.add': 'Buat cabang',
  'bo.branches.listTitle': 'Semua cabang',
  'bo.branches.listDesc': 'Cabang yang dapat Anda akses.',
  'bo.branches.count': '{count} cabang yang dapat Anda akses.',
  'bo.branches.empty': 'Belum ada cabang — buat cabang pertama untuk mulai menyiapkan.',
  'bo.branches.emptyReadOnly':
    'Belum ada cabang. Pemilik atau manajer dengan hak cabang dapat membuatnya.',
  'bo.branches.col.code': 'Kode',
  'bo.branches.col.name': 'Nama',
  'bo.branches.col.timeZone': 'Zona waktu',
  'bo.branches.col.currency': 'Mata uang',
  'bo.branches.col.locale': 'Bahasa',
  'bo.branches.col.status': 'Status',
  'bo.branches.col.createdAt': 'Dibuat',
  'bo.branches.sortBy': 'Urutkan menurut {column}',
  'bo.branches.createTitle': 'Buat cabang',
  'bo.branches.createDesc':
    'Zona waktu dan mata uang tetap setelah mulai berjualan; tanggal bisnis mengikuti zona waktu.',
  'bo.branches.codeHint': '2–16 huruf kapital, angka, atau tanda hubung, mis. JKT-02.',
  'bo.branches.timeZoneHint': 'Hari bisnis dan tanggal laporan memakai zona waktu ini.',
  'bo.branches.submit': 'Buat cabang',
  'bo.branches.created': 'Cabang {name} dibuat.',
  'bo.branches.error.codeRequired': 'Masukkan kode cabang.',
  'bo.branches.error.codeFormat': 'Gunakan 2–16 huruf kapital, angka, atau tanda hubung.',
  'bo.branches.error.codeTaken': 'Cabang dengan kode ini sudah ada.',
  'bo.branches.error.nameRequired': 'Masukkan nama cabang.',
  'bo.branches.error.timeZone': 'Zona waktu tidak dikenal.',
  'bo.branches.error.currency': 'Mata uang ini tidak didukung.',

  // ---- Settings ----------------------------------------------------------------------
  'bo.settings.title': 'Pengaturan',
  'bo.settings.subtitle': 'Pengaturan cabang {branch}.',
  'bo.settings.readOnly':
    'Peran Anda dapat melihat pengaturan ini, tetapi tidak dapat mengubahnya.',
  'bo.settings.cardTitle': 'Penagihan',
  'bo.settings.cardDesc': 'Nama, pajak, biaya layanan, dan pembulatan tunai untuk tagihan baru.',
  'bo.settings.name': 'Nama cabang',
  'bo.settings.tax': 'Pajak (PB1)',
  'bo.settings.service': 'Biaya layanan',
  'bo.settings.percentHint': 'Persen dengan paling banyak 2 desimal, mis. 10 atau 5,5.',
  'bo.settings.percentInvalid': 'Masukkan persentase 0 sampai 50 dengan paling banyak 2 desimal.',
  'bo.settings.bpsHint': 'Disimpan sebagai {bps} basis poin.',
  'bo.settings.rounding': 'Satuan pembulatan ({currency}, satuan terkecil)',
  'bo.settings.roundingHint': 'Total tagihan dibulatkan ke kelipatan ini; 1 = tanpa pembulatan.',
  'bo.settings.roundingNone': 'Tanpa pembulatan.',
  'bo.settings.roundingTo': 'Total dibulatkan ke kelipatan',
  'bo.settings.roundingInvalid': 'Masukkan bilangan bulat dari 1 sampai 100000.',
  'bo.settings.wasteThreshold': 'Batas persetujuan waste ({currency}, satuan terkecil)',
  'bo.settings.wasteThresholdHint':
    'Catatan waste dengan perkiraan nilai sama atau di atas jumlah ini perlu disetujui orang lain.',
  'bo.settings.wasteThresholdAll': '0 = setiap catatan waste perlu persetujuan.',
  'bo.settings.wasteThresholdFrom': 'Perlu persetujuan mulai',
  'bo.settings.wasteThresholdInvalid': 'Masukkan bilangan bulat 0 atau lebih.',
  'bo.settings.separationOfDuties': 'Pemisahan tugas',
  'bo.settings.separationOfDutiesHint':
    'Aktif: orang yang menghitung stok, mencatat waste, atau meminta barang tidak boleh menyetujuinya — harus orang lain. Nonaktif (tim kecil): boleh menyetujui sendiri dengan alasan; dicatat sebagai persetujuan sendiri.',
  'bo.settings.appliesNote':
    'Perubahan berlaku untuk tagihan yang dibuat setelah disimpan; tagihan yang sudah ada tetap memakai tarif lamanya.',
  'bo.settings.submit': 'Simpan pengaturan',
  'bo.settings.saved': 'Pengaturan cabang disimpan.',
  'bo.settings.conflict':
    'Orang lain mengubah pengaturan ini sementara itu. Nilai terbaru sudah dimuat — periksa lalu simpan lagi.',
  // ---- Promo (menu tamu) ---------------------------------------------------------------
  'bo.nav.promotions': 'Promo',
  'bo.promo.title': 'Promo',
  'bo.promo.subtitle':
    'Tampil di bagian atas menu tamu (QR). Promo tidak mengubah harga — diskon diterapkan di kasir.',
  'bo.promo.readOnly': 'Anda dapat melihat promo. Mengubahnya memerlukan hak kelola menu.',
  'bo.promo.new': 'Promo baru',
  'bo.promo.edit': 'Ubah promo',
  'bo.promo.empty': 'Belum ada promo — buat satu agar tampil di menu tamu.',
  'bo.promo.col.promotion': 'Promo',
  'bo.promo.col.window': 'Tayang',
  'bo.promo.col.status': 'Status',
  'bo.promo.field.title': 'Judul',
  'bo.promo.field.description': 'Deskripsi',
  'bo.promo.field.badge': 'Label',
  'bo.promo.field.badgeHint': 'Label singkat di kartu, mis. "Hemat 20%" atau "Baru".',
  'bo.promo.field.image': 'URL gambar (https)',
  'bo.promo.field.item': 'Item menu',
  'bo.promo.field.itemHint': 'Tamu bisa langsung menuju item ini dari promo.',
  'bo.promo.field.noItem': 'Tidak ada',
  'bo.promo.field.startsAt': 'Tayang mulai',
  'bo.promo.field.endsAt': 'Tayang sampai',
  'bo.promo.field.windowHint': 'Kosongkan agar langsung tayang / tayang sampai dimatikan.',
  'bo.promo.always': 'Selalu',
  'bo.promo.from': 'Mulai {date}',
  'bo.promo.until': 'Sampai {date}',
  'bo.promo.between': '{from} – {to}',
  'bo.promo.status.live': 'TAYANG',
  'bo.promo.status.scheduled': 'TERJADWAL',
  'bo.promo.status.ended': 'BERAKHIR',
  'bo.promo.status.off': 'NONAKTIF',
  'bo.promo.created': 'Promo dibuat.',
  'bo.promo.saved': 'Promo disimpan.',
  'bo.promo.activate': 'Aktifkan',
  'bo.promo.deactivate': 'Nonaktifkan',
  'bo.promo.switchedOn': '{title} tampil lagi.',
  'bo.promo.switchedOff': '{title} disembunyikan dari tamu.',
  'bo.promo.error.window': 'Waktu selesai harus setelah waktu mulai.',

  // ---- QR meja -----------------------------------------------------------------------------
  'bo.nav.qr': 'QR meja',
  'bo.qr.title': 'QR meja',
  'bo.qr.subtitle':
    'Setiap meja punya kode sendiri; semuanya membuka menu cabang yang sama. Tamu melihat promo, tagihan mejanya, dan bisa membayar di aplikasi.',
  'bo.qr.print': 'Cetak',
  'bo.qr.open': 'Buka',
  'bo.qr.rotate': 'Kode baru',
  'bo.qr.rotateTitle': 'Ganti kode QR meja {table}?',
  'bo.qr.rotateBody':
    'Kode yang sudah dicetak langsung tidak berlaku. Cetak dan pasang kode baru di meja.',
  'bo.qr.rotated': 'Meja {table} punya kode QR baru. Cetak ulang.',
  'bo.qr.inactive': 'NONAKTIF',
  'bo.qr.scanHint': 'Pindai untuk menu & tagihan',
  'bo.qr.empty': 'Belum ada meja — tambahkan meja di Lantai & meja terlebih dulu.',
  'bo.qr.qrLabel': 'Kode QR meja {table}',
  'bo.reports.method.online': 'Online (aplikasi tamu)',
  'bo.promo.listTitle': 'Semua promo',
  'bo.promo.listDesc':
    'Apa yang tampil di menu tamu dan kapan. Matikan promo untuk menyembunyikannya tanpa menghapus.',
  'bo.promo.searchPlaceholder': 'Judul, label, atau item menu',
  'bo.promo.allStatuses': 'Semua status',
  'bo.promo.editNamed': 'Ubah {title}',
  // ---- List tables: floors, stations, shifts -----------------------------------------
  'bo.list.allStatuses': 'Semua status',
  'bo.list.allActive': 'Aktif & nonaktif',
  'bo.floors.col.tables': 'Meja',
  'bo.floors.search': 'Nama lantai',
  'bo.tables.search': 'Meja atau lantai',
  'bo.tables.allFloors': 'Semua lantai',
  'bo.stations.search': 'Nama stasiun',
  'bo.shifts.search': 'Kasir atau catatan',
  'bo.shifts.allCashiers': 'Semua kasir',
  'bo.shifts.col.note': 'Catatan',
  'bo.shifts.col.payIn': 'Kas masuk',
  'bo.shifts.col.payOut': 'Kas keluar',
  'bo.shifts.col.payments': 'Pembayaran',
};
